/** The course screen's clicker and card section (STORY-088): a collapsed `<details>` in the rail,
 * because the mapping is set once per course. Renders nothing while `deviceChannels` is empty, so
 * `Course()` mounts it with one line and no hook. `initialKey` opens the form on an id, the seam
 * STORY-086's unknown-id list uses. It is read on the first render only (both `useState`
 * initialisers): a caller that offers a new id remounts the panel with `key={initialKey}`.
 * Following the prop with an effect would reset a form the teacher is editing.
 *
 * Clickers are offered on the `clickers` flag, or on any local host (STORY-086, decision 5):
 * there the Teacher Computer app reads the hub itself, and `ClickerReader` shows it. */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { ApiError } from "../api/client";
import type { CourseDevice } from "../api/types";
import { useCardsEnabled } from "../cards/enabled";
import { useFlags } from "../platform/flags";
import { isLocalHost } from "../platform/localHost";
import { useConfirm } from "../ui";
import { deleteCourseDevice, getRoster, listCourseDevices, putCourseDevice } from "./api";
import { ClickerReader } from "./ClickerReader";
import { clickersEnabledFrom, deviceBody, deviceChannels, rowsOffered, type MappableChannel } from "./deviceMap";
import { DeviceMapForm, DeviceMapList, type DeviceFormValues, type Refusal } from "./DeviceMapView";

type PanelProps = { courseId: string; guestOnly: boolean; initialKey?: string; onMapUnknown?: (key: string) => void };

export function DeviceMapPanel({ courseId, guestOnly, initialKey, onMapUnknown }: PanelProps) {
  const flags = useFlags();
  const localHost = isLocalHost();
  // cards on the cloud's flag or on any local host, where nobody can turn a flag on
  const cards = useCardsEnabled();
  const channels = deviceChannels({ clickers: clickersEnabledFrom(flags, localHost), cards });
  if (channels.length === 0) return null;
  // the reader is this computer's: shown only where it runs, beside the clicker rows
  const reader = localHost && channels.includes("clicker") && onMapUnknown ? onMapUnknown : undefined;
  return <Section courseId={courseId} guestOnly={guestOnly} channels={channels} initialKey={initialKey} onMapUnknown={reader} />;
}

function Section({ courseId, guestOnly, channels, initialKey, onMapUnknown }: PanelProps & { channels: MappableChannel[] }) {
  const { t } = useTranslation();
  const confirm = useConfirm();
  const qc = useQueryClient();
  const blank = (key = ""): DeviceFormValues => ({ channel: channels[0]!, key, slot: "seat", seat: "", subject: "", note: "", editing: false });
  const [open, setOpen] = useState(!!initialKey);
  const [values, setValues] = useState<DeviceFormValues>(() => blank(initialKey ?? ""));
  const [refusal, setRefusal] = useState<Refusal | null>(null);
  const formHeading = useRef<HTMLHeadingElement>(null);
  // mounted on an id the reader offered: take the keyboard and the eye to the form
  useEffect(() => {
    if (initialKey) formHeading.current?.focus();
  }, [initialKey]);
  const rows = useQuery({ queryKey: ["courseDevices", courseId], queryFn: () => listCourseDevices(courseId) });
  const roster = useQuery({ queryKey: ["roster", courseId], queryFn: () => getRoster(courseId), enabled: open });
  const refresh = () => qc.invalidateQueries({ queryKey: ["courseDevices", courseId] });
  const refuse = (e: unknown) => setRefusal(e instanceof ApiError ? { code: e.code ?? null, holder: (e.body as { device_key?: string } | undefined)?.device_key } : { code: null });

  const save = useMutation({
    mutationFn: ({ key, body }: { key: string; body: Parameters<typeof putCourseDevice>[3] }) => putCourseDevice(courseId, values.channel, key, body),
    onSuccess: () => {
      setValues(blank());
      setRefusal(null);
      void refresh();
    },
    onError: refuse,
  });
  const remove = useMutation({
    mutationFn: (row: CourseDevice) => deleteCourseDevice(courseId, row.channel, row.device_key),
    onSuccess: () => void refresh(),
    onError: refuse,
  });

  const submit = () => {
    // the same rule the form shows by: Save never sends a slot the form does not show
    const out = deviceBody(values, { guestOnly, roster: roster.data ?? [] });
    if ("refusal" in out) return setRefusal({ code: out.refusal });
    save.mutate(out);
  };

  // only the rows of the channels offered: a laptop lists its card rows and no clicker row
  const list = rowsOffered(rows.data ?? [], channels);
  return (
    <details className="rounded-[var(--radius-card)] bg-[color:var(--surface)] p-4 shadow-sm" open={open} onToggle={(e) => setOpen(e.currentTarget.open)}>
      <summary className="cursor-pointer font-semibold">
        {t(`course.devices.title.${channels.length > 1 ? "both" : channels[0]}`)}
        {list.length > 0 && <span className="ml-2 text-sm font-normal text-[color:var(--ink-2)]">{t("course.devices.count", { count: list.length })}</span>}
      </summary>
      <p className="measure mb-3 mt-1 text-sm text-[color:var(--ink-2)]">{t(`course.devices.hint.${channels.length > 1 ? "both" : channels[0]}`)}</p>
      {rows.isLoading && <p className="text-sm text-[color:var(--ink-2)]">{t("course.devices.loading")}</p>}
      {rows.isError && <p role="alert" className="text-sm">{t("course.devices.loadError")}</p>}
      {rows.data && (
        <DeviceMapList
          rows={list}
          roster={roster.data ?? []}
          guestOnly={guestOnly}
          onChange={(row) => {
            setRefusal(null);
            setValues({
              channel: row.channel,
              key: row.device_key,
              slot: row.seat != null ? "seat" : "student",
              seat: row.seat ?? "",
              subject: row.subject ?? "",
              note: guestOnly ? "" : row.label,
              editing: true,
            });
            // the form sits below the list: take the keyboard and the eye there
            formHeading.current?.focus();
          }}
          onRemove={async (row) => {
            const ok = await confirm({
              title: t("course.devices.removeTitle"),
              body: t("course.devices.removeBody", { key: row.device_key }),
              confirmLabel: t("course.devices.remove"),
              destructive: true,
            });
            if (ok) remove.mutate(row);
          }}
        />
      )}
      <h3 ref={formHeading} tabIndex={-1} className="mb-2 mt-4 scroll-mt-4 font-medium">{values.editing ? t("course.devices.changeTitle") : t("course.devices.add")}</h3>
      <DeviceMapForm
        values={values}
        onChange={(next) => {
          setValues(next);
          setRefusal(null);
        }}
        roster={roster.data ?? []}
        guestOnly={guestOnly}
        channels={channels}
        refusal={refusal}
        pending={save.isPending}
        onSubmit={submit}
        onCancel={values.editing ? () => setValues(blank()) : undefined}
      />
      {onMapUnknown && <ClickerReader courseId={courseId} onMap={onMapUnknown} />}
    </details>
  );
}
